// Chemin: C:\gestion-copro\src\services\extractionService.ts

import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';
import {
  ExtractedLot,
  ExtractedMembreCS,
  LotsExtractionResult,
  CSExtractionResult,
  generateTempId,
} from '../types/extraction.types';

// ============================================
// FIREBASE FUNCTION CALLABLE
// ============================================

interface ExtractDocumentRequest {
  fileBase64: string;
  fileName: string;
  mimeType: string;
  extractionType: 'lots' | 'conseil_syndical';
  coproprieteId: string;
}

// Structure réelle retournée par le backend
interface ExtractDocumentResponse {
  success: boolean;
  extractionType?: 'lots' | 'membres_cs';
  items?: Array<Record<string, unknown>>;
  metadata?: {
    documentName: string;
    extractedAt: string;
    itemCount: number;
    totalTantiemes?: number;
  };
  error?: string;
}

const extractDocumentFunction = httpsCallable<ExtractDocumentRequest, ExtractDocumentResponse>(
  functions,
  'extractDocument'
);

// ============================================
// SERVICE D'EXTRACTION
// ============================================

/**
 * Convertit un fichier en base64
 */
export const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const result = reader.result as string;
      // Retirer le préfixe "data:application/pdf;base64,"
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = (error) => reject(error);
  });
};

/**
 * Extrait les lots d'un règlement de copropriété
 */
export const extractLotsFromDocument = async (
  file: File,
  coproprieteId: string
): Promise<LotsExtractionResult> => {
  try {
    const fileBase64 = await fileToBase64(file);
    
    const response = await extractDocumentFunction({
      fileBase64,
      fileName: file.name,
      mimeType: file.type,
      extractionType: 'lots',
      coproprieteId,
    });

    // La réponse est dans response.data (pas response.data.data)
    const result = response.data;

    if (!result.success || !result.items || result.items.length === 0) {
      return {
        success: false,
        type: 'lots',
        documentName: file.name,
        extractedAt: new Date(),
        items: [],
        totalFound: 0,
        confidence: 'low',
        warnings: [],
        errors: [result.error || 'Aucun lot n\'a pu être extrait de ce document.'],
      };
    }

    // Transformer les données brutes en ExtractedLot
    // Note: le backend retourne "tantiemesGeneraux", pas "tantiemes"
    const items: ExtractedLot[] = result.items.map((item) => ({
      tempId: generateTempId(),
      numero: String(item.numero || ''),
      numeroCommercial: item.numeroCommercial ? String(item.numeroCommercial) : undefined,
      type: String(item.type || 'Logement'),
      batiment: item.batiment ? String(item.batiment) : undefined,
      etage: String(item.etage || 'Non précisé'),
      description: item.description ? String(item.description) : undefined,
      tantiemesGeneraux: Number(item.tantiemesGeneraux) || 0,
      selected: true,
      hasError: false,
    }));

    // Déterminer le niveau de confiance global
    const confidence = items.length > 0 ? 'high' : 'low';

    return {
      success: true,
      type: 'lots',
      documentName: file.name,
      extractedAt: new Date(),
      items,
      totalFound: items.length,
      confidence,
      warnings: [],
      errors: [],
    };
  } catch (error) {
    console.error('Erreur extraction lots:', error);
    return {
      success: false,
      type: 'lots',
      documentName: file.name,
      extractedAt: new Date(),
      items: [],
      totalFound: 0,
      confidence: 'low',
      warnings: [],
      errors: [error instanceof Error ? error.message : 'Erreur lors de l\'extraction'],
    };
  }
};

/**
 * Extrait les membres du CS d'un PV d'AG
 */
export const extractConseilSyndicalFromDocument = async (
  file: File,
  coproprieteId: string
): Promise<CSExtractionResult> => {
  try {
    const fileBase64 = await fileToBase64(file);
    
    const response = await extractDocumentFunction({
      fileBase64,
      fileName: file.name,
      mimeType: file.type,
      extractionType: 'conseil_syndical',
      coproprieteId,
    });

    // La réponse est dans response.data (pas response.data.data)
    const result = response.data;

    if (!result.success || !result.items || result.items.length === 0) {
      return {
        success: false,
        type: 'conseil_syndical',
        documentName: file.name,
        extractedAt: new Date(),
        items: [],
        totalFound: 0,
        confidence: 'low',
        warnings: [],
        errors: [result.error || 'Aucun membre du conseil syndical n\'a pu être extrait.'],
      };
    }

    // Transformer les données brutes en ExtractedMembreCS
    const items: ExtractedMembreCS[] = result.items.map((item) => ({
      tempId: generateTempId(),
      nom: String(item.nom || ''),
      prenom: item.prenom ? String(item.prenom) : undefined,
      role: (item.fonction === 'president' ? 'president' : 'membre') as ExtractedMembreCS['role'],
      dateElection: item.dateElection ? String(item.dateElection) : undefined,
      dateFinMandat: item.dateFinMandat ? String(item.dateFinMandat) : undefined,
      lotId: undefined,
      lotNumero: undefined,
      email: item.email ? String(item.email) : undefined,
      telephone: item.telephone ? String(item.telephone) : undefined,
      selected: true,
      hasError: false,
    }));

    return {
      success: true,
      type: 'conseil_syndical',
      documentName: file.name,
      extractedAt: new Date(),
      items,
      totalFound: items.length,
      confidence: 'high',
      warnings: [],
      errors: [],
    };
  } catch (error) {
    console.error('Erreur extraction CS:', error);
    return {
      success: false,
      type: 'conseil_syndical',
      documentName: file.name,
      extractedAt: new Date(),
      items: [],
      totalFound: 0,
      confidence: 'low',
      warnings: [],
      errors: [error instanceof Error ? error.message : 'Erreur lors de l\'extraction'],
    };
  }
};

// ============================================
// VALIDATION DES DONNÉES EXTRAITES
// ============================================

/**
 * Vérifie qu'un lot extrait est valide
 */
export const validateExtractedLot = (lot: ExtractedLot): ExtractedLot => {
  const errors: string[] = [];
  
  if (!lot.numero || lot.numero.trim() === '') {
    errors.push('Numéro de lot manquant');
  }
  
  if (!lot.tantiemesGeneraux || lot.tantiemesGeneraux <= 0) {
    errors.push('Tantièmes invalides');
  }
  
  if (!lot.etage || lot.etage.trim() === '') {
    errors.push('Étage manquant');
  }
  
  return {
    ...lot,
    hasError: errors.length > 0,
    errorMessage: errors.length > 0 ? errors.join(', ') : undefined,
  };
};

/**
 * Vérifie qu'un membre CS extrait est valide
 */
export const validateExtractedMembreCS = (membre: ExtractedMembreCS): ExtractedMembreCS => {
  const errors: string[] = [];
  
  if (!membre.nom || membre.nom.trim() === '') {
    errors.push('Nom manquant');
  }
  
  return {
    ...membre,
    hasError: errors.length > 0,
    errorMessage: errors.length > 0 ? errors.join(', ') : undefined,
  };
};

/**
 * Valide tous les lots d'un résultat d'extraction
 */
export const validateAllLots = (result: LotsExtractionResult): LotsExtractionResult => {
  const validatedItems = result.items.map(validateExtractedLot);
  const hasErrors = validatedItems.some((item) => item.hasError);
  
  return {
    ...result,
    items: validatedItems,
    warnings: hasErrors 
      ? [...result.warnings, 'Certains lots contiennent des erreurs à corriger']
      : result.warnings,
  };
};

/**
 * Valide tous les membres CS d'un résultat d'extraction
 */
export const validateAllMembresCS = (result: CSExtractionResult): CSExtractionResult => {
  const validatedItems = result.items.map(validateExtractedMembreCS);
  const hasErrors = validatedItems.some((item) => item.hasError);
  
  return {
    ...result,
    items: validatedItems,
    warnings: hasErrors 
      ? [...result.warnings, 'Certains membres contiennent des erreurs à corriger']
      : result.warnings,
  };
};

// ============================================
// CALCULS UTILITAIRES
// ============================================

/**
 * Calcule le total des tantièmes des lots sélectionnés
 */
export const calculateTotalTantiemes = (lots: ExtractedLot[]): number => {
  return lots
    .filter((lot) => lot.selected)
    .reduce((total, lot) => total + (lot.tantiemesGeneraux || 0), 0);
};

/**
 * Compte les lots sélectionnés
 */
export const countSelectedLots = (lots: ExtractedLot[]): number => {
  return lots.filter((lot) => lot.selected).length;
};

/**
 * Compte les membres CS sélectionnés
 */
export const countSelectedMembresCS = (membres: ExtractedMembreCS[]): number => {
  return membres.filter((m) => m.selected).length;
};